import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Cartao,
  CartaoIndicador,
  CampoDeSelecao,
  EstadoCarregando,
  EstadoErro,
} from '../../componentes/ui';
import { BotaoExtrairXlsx } from '../../componentes/BotaoExtrairXlsx';
import {
  CHAVE_DOS_QUADROS,
  carregarQuadros,
  carregarRelatorioDoFunil,
  gravarUltimoQuadro,
  lerUltimoQuadro,
  type CardParado,
  type EtapaDoFunil,
  type RelatorioDoFunil,
} from '../../servicos/crm';
import { aba, type AbaPronta } from '../../servicos/planilha';
import { Pagina } from '../Carteira';

const OPCOES_DE_PARADO = [7, 14, 30, 60] as const;

/**
 * CRM → Relatórios: como o funil está andando.
 *
 * ## Uma cor só
 *
 * Cada gráfico mede uma grandeza (cards, dias) de uma série só: barras numa
 * cor, valor na ponta, nome à esquerda. Cor por etapa faria parecer que as
 * etapas são categorias a distinguir, quando o que importa é o tamanho.
 *
 * ## O histórico começou agora
 *
 * Conversão e tempo em etapa saem do histórico de movimentação, que passou a
 * ser gravado em 30/09/2026. A tela diz desde quando há dado, para um número
 * baixo não ser lido como funil ruim quando é só histórico curto.
 */
export function PaginaDeRelatorios(): JSX.Element {
  const [diasParado, setDiasParado] = useState<number>(14);
  // O funil é de um quadro: cada quadro tem as suas etapas. Abre no último
  // quadro usado no Pipeline (ou no padrão, se ele não existe mais).
  const quadros = useQuery({ queryKey: CHAVE_DOS_QUADROS, queryFn: carregarQuadros });
  const [escolhido, setEscolhido] = useState<string | null>(() => lerUltimoQuadro());
  const lista = quadros.data ?? [];
  const quadroId =
    escolhido !== null && lista.some((q) => q.id === escolhido)
      ? escolhido
      : (lista[0]?.id ?? null);

  const consulta = useQuery({
    queryKey: ['crm', 'relatorio', quadroId, diasParado],
    queryFn: () => carregarRelatorioDoFunil(diasParado, quadroId),
    enabled: quadros.isSuccess,
    placeholderData: (anterior) => anterior,
  });

  const r = consulta.data;

  return (
    <Pagina
      titulo="Relatórios do funil"
      descricao="Quantas empresas há em cada etapa do quadro, quantas avançam, quanto tempo ficam em cada uma e quais estão paradas."
      acao={
        <div className="flex flex-wrap items-end gap-3">
          {lista.length > 1 && (
            <CampoDeSelecao
              rotulo="Quadro"
              className="w-56"
              value={quadroId ?? ''}
              onChange={(e) => {
                setEscolhido(e.target.value);
                gravarUltimoQuadro(e.target.value);
              }}
              opcoes={lista.map((q) => ({ valor: q.id, rotulo: q.nome }))}
            />
          )}
          <BotaoExtrairXlsx
            titulo={
              r === undefined ? 'Relatórios do funil' : `Relatórios do funil - ${r.quadro.nome}`
            }
            disabled={r === undefined}
            abas={() => (r === undefined ? [] : abasDoRelatorio(r, diasParado))}
          />
        </div>
      }
    >
      {consulta.isError ? (
        <EstadoErro
          titulo="Não foi possível carregar o relatório"
          mensagem={consulta.error.message}
          aoTentarNovamente={() => void consulta.refetch()}
        />
      ) : r === undefined ? (
        <EstadoCarregando contexto="o relatório" />
      ) : (
        <div className="flex flex-col gap-6">
          <p className="text-xs text-conteudo-sutil">
            {r.historicoDesde === null
              ? 'Ainda não há movimentações registradas: conversão e tempo em etapa começam a contar quando os cards andarem pelo pipeline.'
              : `Histórico de movimentação desde ${new Date(r.historicoDesde).toLocaleDateString('pt-BR')}. Cards de antes disso entram só com a etapa em que estão.`}
          </p>

          <div className="grid gap-4 sm:grid-cols-3">
            <CartaoIndicador
              rotulo="Empresas no pipeline"
              valor={<span className="tabular-nums">{r.totalDeCards}</span>}
            />
            <CartaoIndicador
              rotulo={`Paradas há ${diasParado}+ dias`}
              valor={<span className="tabular-nums">{r.parados.length}</span>}
              detalhe="Na mesma coluna, sem contar a última etapa."
            />
            <CartaoIndicador
              rotulo="Atividades atrasadas"
              valor={<span className="tabular-nums">{r.atividadesAtrasadas}</span>}
              detalhe={
                r.atividadesAtrasadas > 0 ? (
                  <Link to="/crm/atividades?de=todos" className="underline underline-offset-2">
                    Ver atividades
                  </Link>
                ) : (
                  'Nenhuma vencida.'
                )
              }
            />
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <Cartao
              como="section"
              titulo="Empresas por etapa"
              subtitulo="Onde cada card está agora."
            >
              <Barras
                etapas={r.etapas}
                valor={(e) => e.cards}
                rotuloDoValor={(v) => `${v}`}
                descricao={(e) => `${e.nome}: ${e.cards} ${e.cards === 1 ? 'empresa' : 'empresas'}`}
              />
            </Cartao>

            <Cartao
              como="section"
              titulo="Tempo médio em cada etapa"
              subtitulo="Dias por passagem, contando quem ainda está na etapa."
            >
              <Barras
                etapas={r.etapas}
                valor={(e) => e.tempoMedioDias}
                rotuloDoValor={(v) => `${v.toLocaleString('pt-BR')} ${v === 1 ? 'dia' : 'dias'}`}
                descricao={(e) =>
                  e.tempoMedioDias === null
                    ? `${e.nome}: nenhuma passagem ainda`
                    : `${e.nome}: ${e.tempoMedioDias.toLocaleString('pt-BR')} dias em média, ${e.passagens} ${e.passagens === 1 ? 'passagem' : 'passagens'}`
                }
              />
            </Cartao>
          </div>

          <Cartao
            como="section"
            titulo="Conversão entre etapas"
            subtitulo="Dos cards que chegaram a uma etapa, quantos chegaram à seguinte. Arrastar um card pulando colunas conta como ter passado por elas."
          >
            <Conversao etapas={r.etapas} />
          </Cartao>

          <Cartao
            como="section"
            titulo="Empresas paradas"
            subtitulo="Na mesma coluna há mais tempo que o limite. A última etapa não entra: ficar nela é o esperado."
            acao={
              <CampoDeSelecao
                rotulo="Parado há"
                rotuloOculto
                value={String(diasParado)}
                onChange={(e) => setDiasParado(Number(e.target.value))}
                opcoes={OPCOES_DE_PARADO.map((d) => ({ valor: String(d), rotulo: `${d}+ dias` }))}
              />
            }
          >
            {r.parados.length === 0 ? (
              <p className="text-sm text-conteudo-secundario">
                Nenhuma empresa parada há {diasParado} dias ou mais.
              </p>
            ) : (
              <ul className="flex flex-col divide-y divide-borda/60">
                {r.parados.map((p) => (
                  <li
                    key={p.cardId}
                    className="flex flex-wrap items-center justify-between gap-3 py-2.5"
                  >
                    <span className="flex min-w-0 flex-col">
                      <Link
                        to={`/crm/pipeline?quadro=${r.quadro.id}&card=${p.cardId}`}
                        className="text-sm text-conteudo-primario underline-offset-2 hover:underline"
                      >
                        {p.nomeFantasia?.trim() || p.razaoSocial}
                      </Link>
                      <span className="text-xs text-conteudo-sutil">{p.colunaNome}</span>
                    </span>
                    <span className="text-sm font-medium tabular-nums text-conteudo-secundario">
                      {p.dias} dias
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Cartao>
        </div>
      )}
    </Pagina>
  );
}

/**
 * Barras horizontais de uma série: nome à esquerda, barra fina (20px) com a
 * ponta arredondada e o valor em texto neutro na ponta. Etapa sem valor fica
 * sem barra e diz "—", em vez de uma barra de zero que parece dado.
 */
function Barras(props: {
  readonly etapas: readonly EtapaDoFunil[];
  readonly valor: (e: EtapaDoFunil) => number | null;
  readonly rotuloDoValor: (v: number) => string;
  readonly descricao: (e: EtapaDoFunil) => string;
}): JSX.Element {
  const maximo = Math.max(1, ...props.etapas.map((e) => props.valor(e) ?? 0));

  return (
    <ul className="flex flex-col gap-3">
      {props.etapas.map((e) => {
        const v = props.valor(e);
        return (
          <li
            key={e.colunaId}
            className="grid grid-cols-[minmax(0,9rem)_1fr] items-center gap-3"
            title={props.descricao(e)}
          >
            <span className="truncate text-sm text-conteudo-secundario">{e.nome}</span>
            <span className="flex items-center gap-2">
              {v !== null && v > 0 && (
                <span
                  aria-hidden="true"
                  className="h-5 rounded-r bg-acento-indigo"
                  style={{ width: `${(v / maximo) * 85}%` }}
                />
              )}
              <span className="text-xs tabular-nums text-conteudo-primario">
                {v === null ? '—' : props.rotuloDoValor(v)}
              </span>
              <span className="sr-only">{props.descricao(e)}</span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * A conversão como tabela: é uma razão entre duas contagens, e a tabela mostra
 * as duas — um percentual sozinho esconde se foi 1 de 2 ou 50 de 100. O
 * medidor ao lado é o mesmo percentual, sobre um trilho da mesma cor.
 */
function Conversao(props: { readonly etapas: readonly EtapaDoFunil[] }): JSX.Element {
  const pares = props.etapas.slice(0, -1).map((de, i) => ({ de, para: props.etapas[i + 1]! }));
  if (pares.length === 0) {
    return <p className="text-sm text-conteudo-secundario">O pipeline tem uma etapa só.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[32rem] text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-conteudo-sutil">
            <th className="pb-2 font-medium">De → para</th>
            <th className="pb-2 text-right font-medium">Chegaram</th>
            <th className="pb-2 text-right font-medium">Seguiram</th>
            <th className="pb-2 pl-4 font-medium">Conversão</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-borda/60">
          {pares.map(({ de, para }) => {
            const taxa = de.alcancaram === 0 ? null : para.alcancaram / de.alcancaram;
            return (
              <tr key={de.colunaId}>
                <td className="py-2.5 text-conteudo-primario">
                  {de.nome} → {para.nome}
                </td>
                <td className="py-2.5 text-right tabular-nums">{de.alcancaram}</td>
                <td className="py-2.5 text-right tabular-nums">{para.alcancaram}</td>
                <td className="py-2.5 pl-4">
                  {taxa === null ? (
                    <span className="text-conteudo-sutil">—</span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <span
                        className="h-2 w-24 overflow-hidden rounded-full bg-acento-indigo/15"
                        aria-hidden="true"
                      >
                        <span
                          className="block h-full rounded-full bg-acento-indigo"
                          style={{ width: `${Math.round(taxa * 100)}%` }}
                        />
                      </span>
                      <span className="tabular-nums text-conteudo-primario">
                        {Math.round(taxa * 100)}%
                      </span>
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** As duas tabelas do relatório, cada uma numa aba: as etapas e as empresas paradas. */
function abasDoRelatorio(r: RelatorioDoFunil, diasParado: number): AbaPronta[] {
  const etapas = r.etapas;
  const conversao = (e: EtapaDoFunil): number | null => {
    const proxima = etapas[etapas.indexOf(e) + 1];
    return proxima === undefined || e.alcancaram === 0
      ? null
      : Math.round((proxima.alcancaram / e.alcancaram) * 1000) / 10;
  };
  return [
    aba(
      'Etapas',
      [
        { titulo: 'Etapa', largura: 24, valor: (e) => e.nome },
        { titulo: 'Empresas na etapa', formato: 'numero', valor: (e) => e.cards },
        { titulo: 'Chegaram à etapa', formato: 'numero', valor: (e) => e.alcancaram },
        {
          titulo: 'Seguiram para a próxima',
          formato: 'percentual',
          largura: 16,
          valor: conversao,
        },
        { titulo: 'Tempo médio (dias)', formato: 'numero', valor: (e) => e.tempoMedioDias },
        { titulo: 'Passagens', formato: 'numero', valor: (e) => e.passagens },
      ],
      etapas,
    ),
    aba<CardParado>(
      `Paradas há ${diasParado}+ dias`,
      [
        { titulo: 'Empresa', largura: 44, valor: (p) => p.nomeFantasia?.trim() || p.razaoSocial },
        { titulo: 'Etapa', largura: 24, valor: (p) => p.colunaNome },
        { titulo: 'Dias na etapa', formato: 'numero', valor: (p) => p.dias },
      ],
      r.parados,
    ),
  ];
}
